/// <reference types="moment" />

import { } from './Types/hicomGlobalTypes';

/* ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄

						Strings

   ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄  */

class StringExtensions {
	static NullIfEmpty(value: string): string {
		if (value == "") {
			return null;
		}
		return value.toString();
    }

    /** Mixin to extend the String type with a method to escape unsafe characters
     *  for use in HTML.  Uses OWASP guidelines for safe strings in HTML.
     * 
     *  Credit: http://benv.ca/2012/10/4/you-are-probably-misusing-DOM-text-methods/
     *          https://github.com/janl/mustache.js/blob/16ffa430a111dc293cd9ed899ecf9da3729f58bd/mustache.js#L62
     *
     *  Maintained by stevejansen_github@icloud.com
     *
     *  @license http://opensource.org/licenses/MIT
     *
     *  @version 1.0
     *
     *  @mixin
     */
    static escapeHtml(value: string): string {
        return value.replace(/[&<>"'\/]/g, function (s) {
            var entityMap = {
                "&": "&amp;",
                "<": "&lt;",
                ">": "&gt;",
                '"': '&quot;',
                "'": '&#39;',
                "/": '&#x2F;'
            };

            return entityMap[s];
        });
    }

    static isValidJSON(jsonString: string): boolean {
       
        try {
            
            // Handle non-exception-throwing cases:
            // Neither JSON.parse(false) or JSON.parse(1234) throw errors, hence the type-checking,
            // but... JSON.parse(null) returns null, and typeof null === "object", 
            // so we must check for that, too. Thankfully, null is falsey, so this suffices:            
            const parsed = JSON.parse(jsonString);
            return parsed != null && typeof parsed === "object";

        } catch (e) {
            return false; // If an error is thrown, it's invalid JSON
        }
    }
}

String.prototype.NullIfEmpty = function (): string {
	return StringExtensions.NullIfEmpty(this);
};

String.prototype.escapeHtml = function (): string {
    return StringExtensions.escapeHtml(this);
};

String.prototype.isValidJSON = function (): boolean {
    return StringExtensions.isValidJSON(this);
};

/* ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄ End Strings ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄ */


/* ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄

						Colors - RGB, HEX

   ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄  */

/**
 * Determines the appropriate contrasting text color (black or white) for a given background color.
 * 
 * This function converts the provided hexadecimal background color to its RGB representation,
 * calculates the contrast ratio between the background color and both black and white text colors,
 * and returns the color with sufficient contrast.
 * 
 * @param {string} bgColor - The background color in hexadecimal format.
 * @returns {string} - The contrasting text color in hexadecimal format ('#000000' for black or '#FFFFFF' for white).
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getContrastingTextColor(bgColor: string): string {
    const backgroundRgb: number[] = hexToRgb(bgColor);
    // Assume black and white as potential text colors
    const blackRgb: number[] = [0, 0, 0];

    const blackContrast: number = contrastRatio(backgroundRgb, blackRgb);

    // Return the color with sufficient contrast
    return blackContrast >= 4.5 ? '#000000' : '#FFFFFF';
}

/**
 * Calculates the contrast ratio between two colors based on their luminance values.
 * 
 * This function takes two RGB color arrays, computes their luminance values, and then
 * calculates the contrast ratio using the formula provided by the WCAG guidelines.
 * The contrast ratio is a measure of the difference in perceived brightness between
 * the two colors, which is important for ensuring text readability and accessibility.
 * 
 * @param {number[]} rgb1 - The first color as an array of RGB values [r, g, b].
 * @param {number[]} rgb2 - The second color as an array of RGB values [r, g, b].
 * @returns {number} - The contrast ratio between the two colors.
 */
function contrastRatio(rgb1: number[], rgb2: number[]): number {
    const l1 = luminance(rgb1);
    const l2 = luminance(rgb2);
    return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

/**
 * Converts a hex color code to an RGB array.
 * 
 * This function takes a hex color code as input, removes the leading '#' if present,
 * and converts it to an RGB array. If the hex code is in shorthand (3 characters), 
 * it expands it to the full 6 characters before conversion.
 * 
 * @param {string} hex - The hex color code to convert.
 * @returns {number[]} - An array containing the RGB values.
 */
function hexToRgb(hex: string): number[] {
    if (hex === undefined || hex === null || hex === '') return [];
    hex = hex.replace(/^#/, '');
    if (hex.length === 3) {
        hex = hex.split('').map(char => char + char).join('');
    }
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return [r, g, b];
}

/**
 * Calculate the relative luminance of an RGB color.
 * 
 * This function takes an array of RGB values, normalizes them, and then applies
 * the formula for relative luminance as defined by the W3C. The resulting luminance
 * value can be used to determine the contrast ratio between colors.
 * 
 * @param {number[]} rgb - An array containing the red, green, and blue color values.
 * @returns {number} - The calculated relative luminance.
 */
function luminance(rgb: number[]): number {
    const [r, g, b] = rgb.map(value => {
        value /= 255;
        return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/* ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄ End Colors - RGB, HEX ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄ */

/* ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄

                        Numbers

   ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄  */
/**
* Checks if the given value is numeric.
* 
* This function first attempts to parse the value as a float and then performs 
* a check to determine if the parsed value is a valid number. If the value can 
* be successfully parsed and is a valid number, it returns true. Otherwise, it returns false.
* 
* This is a native JavaScript alternative to jQuery.isNumeric(), which is deprecated.
* 
* @param {string | number} value - The value to check if it's numeric.
* @returns {boolean} - Returns true if the value is numeric, false otherwise.
*/
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function isNumeric(value: string | number): boolean {
    if (typeof value === 'number') {
        return !isNaN(value);
    }
    if (typeof value === 'string') {
        return !isNaN(parseFloat(value));
    }
    return false;
}

/* ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄ End of numbers ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄ */


/* ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄

                        Array helper methods

   ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄  */

const PIPE_DELIM: string = "|";

/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Get array from pipe record delimited text
 * @param {string} val
 */
function getArray(val: string): string[] {
    return String(val).split(PIPE_DELIM);
}

/**
 * Array contains single value
 * @param {string} arr - The array in string format.
 * @param {string} val - The value to check.
 * @returns {boolean} - Returns true if the array contains the value, otherwise false.
 */
function containsValue(arr: string, val: string): boolean {
    return getArray(arr).some(arrVal => val == arrVal);
}

/**
 * Array contains all values
 * @param {string} arr - The array in string format.
 * @param {string} val - The values to check.
 * @returns {boolean} - Returns true if the array contains all the values, otherwise false.
 */
function containsAllValues(arr: string, val: string) {
    return getArray(val).every(v => getArray(arr).includes(v));
}

/**
 * Array does not contain the single value
 * @param {string} arr - The array in string format.
 * @param {string} val - The value to check.
 * @returns {boolean} - Returns true if the array does not contain the value, otherwise false.
 */
function doesNotContainValue(arr: string, val: string): boolean {
    return !getArray(arr).some(arrVal => val == arrVal);
}

/**
 * Array does not contain all the values
 * @param {string} arr - The array in string format.
 * @param {string} val - The values to check.
 * @returns {boolean} - Returns true if the array does not contain all the values, otherwise false.
  */
function doesNotContainAllValues(arr: string, val: string) {
    return !getArray(val).every(v => getArray(arr).includes(v));
}

/**
 * Array contains any of the multiple values
 * @param {string} arr - The array in string format.
 * @param {string} val - The values to check.
 * @returns {boolean} - Returns true if the array contains any of the values, otherwise false.
 */
function containsAnyValue(arr: string, val: string): boolean {
    return getArray(arr).some(v => getArray(val).indexOf(v) !== -1);
}


/**
 * Does the value exist?
 * @param {string} controllerType - The type of controller (e.g., 'checkbox').
 * @param {any} value - The value to check.
 * @returns {boolean} - Returns true if the value exists or is valid, otherwise false.
 */
function populated(controllerType: string, value: string | number | object): boolean {
    switch (controllerType) {
        case 'checkbox':
            return String(value) !== 'off';

        default:
            return String(value) !== '';
    }
}

/**
 * Array does not contain any of the multiple values
 * @param {string} arr - The array in string format.
 * @param {string} val - The values to check.
 * @returns {boolean} - Returns true if the array does not contain any of the values, otherwise false.
 */
function doesNotContainAnyValue(arr: string, val: string): boolean {
    return !getArray(arr).some(v => getArray(val).indexOf(v) !== -1);
}
/* eslint-enable @typescript-eslint/no-unused-vars */

/* ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄ End of array helper methods ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄ */


/* ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄

                        Browser support feature detection methods

   ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄  */

//Detect browsers using feature detection and user agent strings
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-var */
/* eslint-disable @typescript-eslint/no-unused-vars */
var is_opera = !!(window as any).opera || navigator.userAgent.indexOf(' OPR/') >= 0;
var is_Edge = navigator.userAgent.indexOf("Edge") > -1;
var is_chrome = !!(window as any).chrome && !is_opera && !is_Edge;
var is_explorer = typeof document !== 'undefined' && !!(document as any).documentMode && !is_Edge;
var is_firefox = /firefox/i.test(navigator.userAgent);
var is_safari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

/**
 * Check if the browser supports the datetime-local input type.
 * @returns {boolean} - Returns true if the browser supports the datetime-local input type, otherwise false.
 */

/* eslint-disable @typescript-eslint/no-unused-vars */
function isInputTypeDateTimeLocalSupported(): boolean {
    if (is_firefox || is_safari) {
        return false;
    } else {
        return true;
    }
}

/* ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄ End of browser support feature detection methods ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄ */
